import React from 'react';
import { useNetwork } from '../../context/NetworkContext';

/**
 * Global offline/online status bar.
 * - When OFFLINE: shows a persistent red banner at the top
 * - When back online: shows green "Back online" banner for 3s
 * - Uses safe-area-top so it sits below the OS status bar on iOS/Android
 *
 * Also renders the specific-action offline toast (anchored bottom-center).
 */
const OfflineBanner = () => {
  const { isOnline, wasOffline, toast, dismissToast } = useNetwork();

  return (
    <>
      {/* Top banner */}
      {!isOnline && (
        <div
          className="fixed left-0 right-0 z-[100] bg-amber-500/95 text-gray-900 text-sm font-semibold py-2 px-4 text-center shadow-lg pt-safe"
          style={{ top: 0 }}
          data-testid="offline-banner"
          role="alert"
        >
          <span className="inline-flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M8.5 16.5a5 5 0 017 0M5 13a9 9 0 019-3M2 8.82a15 15 0 014-2.3M15.1 10.2a9 9 0 014.9 2.8M19 5.2a15 15 0 01-1.4 1.1" />
            </svg>
            No internet connection — some actions are disabled
          </span>
        </div>
      )}

      {/* Back-online banner (only shows right after reconnection) */}
      {isOnline && wasOffline && (
        <div
          className="fixed left-0 right-0 z-[100] bg-green-500/95 text-white text-sm font-medium py-2 px-4 text-center shadow-lg pt-safe"
          style={{ top: 0 }}
          data-testid="online-banner"
          role="status"
        >
          <span className="inline-flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Back online
          </span>
        </div>
      )}

      {/* Specific action offline toast (bottom center) */}
      {toast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[101] bg-amber-500/95 text-gray-900 text-sm font-semibold px-4 py-3 rounded-xl shadow-2xl max-w-sm text-center pb-safe-extra"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)' }}
          data-testid="offline-action-toast"
          role="alert"
          onClick={dismissToast}
        >
          {toast.msg}
        </div>
      )}
    </>
  );
};

export default OfflineBanner;
